import { useRef, useState, useEffect } from 'react';
import { Blog, City, Tag, Country, Pagination as TPagination, FilterOption } from 'src/types';
import { ApiRoutes } from 'src/lib/routes';
import { getEleOffset } from 'src/helpers';
import useQuery from 'src/hooks/useQuery';
import BlogCardV2, { BlogCardSkeleton } from '../BlogCardV2';
import FilterCommonItem from 'src/components/filter/CommonItem';
import Loading from '../Loading';
import { min, range, sortBy } from 'lodash-es';
import Pagination from '../Pagination';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

type Props = {
  country?: Country | null;
  countries?: Country[];
  blogsCount?: number;
  onPageChange?: () => void;
};

const ReadAllBlog: React.FC<Props> = ({ country, countries, blogsCount, onPageChange }) => {
  const [filterParams, setFilterParams] = useState<{ cityIds: number[]; topicTagIds: string[] }>({
    cityIds: [] as number[],
    topicTagIds: [],
  });
  const translation = useTranslations('components.blog.ReadAllBlog')
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const scrollLoadMoreRef = useRef<HTMLDivElement>(null);
  const fallbackData: { data: Blog[]; pagination: TPagination } = {
    data: [],
    pagination: {} as TPagination,
  };
  const numSkeletons = min([blogsCount || 0, 9]) as number
  const { page: pageQuery } = router.query;
  const { data: kindsData } = useQuery<{ data: { topics: Tag[], cities: City[]; } }>(
    ApiRoutes.apiBlogFilterItemsRoute(
      {},
      {
        country_id: country?.id
      }
    ).toUrl()
  )

  const { data: kindsTmp } = kindsData || {};
  const { topics = [], cities = [] } = kindsTmp || {}

  const { data, isLoading } = useQuery<{ data: Blog[]; pagination: TPagination }>(
    ApiRoutes.apiBlogsRoute(
      {},
      {
        topic_tag_ids: filterParams.topicTagIds,
        city_ids: filterParams.cityIds,
        country_id: country?.id,
        page: page,
        per_page: 9,
      }
    ).toUrl()
  );

  const { data: fetchedBlogs, pagination } = data || fallbackData;

  const topicTagOptions: FilterOption[] = topics
    ? topics.map((tag) => {
        return {
          label: tag.name,
          value: tag.id,
        };
      })
    : [];
  const cityFilterOptions = (countries || []).filter(o => country ? o.id === country.id : true).reduce((memo, country) => {
    memo.push({
      label: country.name,
      value: `country_${country.id}`,
      children: sortBy(cities, 'name')
        .filter((o) => o.countryId === country.id)
        .map((city) => ({
          label: city.name,
          value: city.id,
        })),
    });
    return memo;
  }, [] as FilterOption[]);

  useEffect(() => {
    if (page > 1) {
      if (!isLoading) {
        setBlogs((prev) => [...fetchedBlogs]);
      }
    } else {
      setBlogs(fetchedBlogs);
    }
  }, [fetchedBlogs.map((o) => o.id).join(','), isLoading]);

  useEffect(()=> {
    if (pageQuery && Number(pageQuery)) {
      if (Number(pageQuery) !== page) {
        setPage(Number(pageQuery));
      }
    }
  }, [pageQuery]);

  return (
    <>
      <div className="tw-flex tw-items-center md:tw-justify-center tw-flex-nowrap tw-overflow-x-auto tw-mb-12 md:tw-mb-16 custom-scrollbar">
        {cityFilterOptions.length > 0 ? (
          <FilterCommonItem
            inputType="checkbox"
            options={cityFilterOptions}
            label={translation('city')}
            fieldName="city"
            isNested={true}
            hideCounter={true}
            onChange={(values) => {
              setPage(1)
              setFilterParams((prev) => ({
                ...prev,
                cityIds:
                  typeof values === 'object' && 'includes' in values
                    ? (values
                        .filter((v) => !String(v).startsWith('country_'))
                        .map((v) => Number(v)) as number[])
                    : [],
              }));
            }}
          />
        ) : null}

        {topicTagOptions.length > 0 ? (
          <FilterCommonItem
            inputType="checkbox"
            label={translation('category')}
            fieldName="topicTagIds"
            options={topicTagOptions}
            defaultValue={[]}
            onChange={(topicTagId) => {
              setPage(1);
              setFilterParams((prev) => ({
                ...prev,
                topicTagIds: topicTagId as string[],
              }));
            }}
          />
        ) : null}
      </div>

      {blogs.length > 0 ? (
        <>
          <div className="row row-original">
            {blogs.map((blog) => (
              <div key={blog.id} className="col-md-4 tw-mb-8 md:tw-mb-0">
                <BlogCardV2 blog={blog} />
              </div>
            ))}
          </div>

          {isLoading ? (
            <div className="tw-mt-10">
              <Loading className="tw-justify-center" />
            </div>
          ) : null}

          <div className='tw-flex tw-justify-center tw-mt-8'>
            {pagination.totalPages > 1 ? (
              <Pagination
                paginationData={pagination}
                window={2}
                onClick={(page) => {
                  router.push({
                    pathname: router.pathname,
                    query: { ...router.query, page }
                  }, undefined, { shallow: true });

                  if (typeof onPageChange === 'function') {
                    onPageChange();
                  }
                }}
              />
            ) : null}
          </div>
        </>
      ) : isLoading ? numSkeletons > 0 ? (
        <div className="row row-original">
          {range(numSkeletons).map((i) => (
            <div key={i} className="col-md-4 tw-mb-8 md:tw-mb-0">
              <BlogCardSkeleton />
            </div>
          ))}
        </div>
      ) : (
        <div className="tw-mt-10">
          <Loading className="tw-justify-center" />
        </div>
      ) : (
        <div className="md:tw-text-xl tw-text-center">{translation('seemsLikeTheseArticles')}</div>
      )}
      <div ref={scrollLoadMoreRef}></div>
    </>
  );
};

export default ReadAllBlog;
